import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/apiService';
import { Helmet } from 'react-helmet';
import Select from 'react-select';
import debounce from 'lodash.debounce';


const UserForm = () => {
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editedUser, setEditedUser] = useState({});
  const getUserById = (id) => users.find(u => String(u.id) === String(id));

  // NEW: Kontakte (abhängig vom gewählten Kunden im Edit-Mode)
  const [customerContacts, setCustomerContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);

   // ▼ NEU: Optionen für Multi-Selects
 const [productGroups, setProductGroups] = useState([]);   // [{id,name}]
 const [machineTypes, setMachineTypes]   = useState([]);   // [{id,name}]

  // ⬇️ Admin-Delete-States
const [showAdminDeleteModal, setShowAdminDeleteModal] = useState(false);
const [adminDeletePassword, setAdminDeletePassword] = useState('');
const [adminDeleteError, setAdminDeleteError] = useState('');
const [adminDeleting, setAdminDeleting] = useState(false);
const [adminToDelete, setAdminToDelete] = useState(null);

// ⬇️ Admin-Erkennung (an dein Modell anpassen)
const isAdminAccount = (u) =>
  (u?.role && String(u.role).toLowerCase() === 'admin') ||
  (u?.username && String(u.username).toLowerCase() === 'admin') ||
  u?.isAdmin === true;


  // Lookups
  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error);
      alert('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
  try {
    const res = await api.get('/customers');
    const rows = res.data || [];
    setCustomers(rows);
    setFilteredCustomers(rows); // NEU: initial alle Kunden anzeigen
  } catch (error) {
    console.error('Fehler beim Laden der Kunden:', error);
  }
};


   // ▼ NEU: Produktgruppen laden (nutzt deine bestehende /product-groups/used)
 const fetchProductGroups = async () => {
   try {
     const res = await api.get('/product-groups/used');
     // Erwartet [{id,name,color?...}]
     const rows = Array.isArray(res.data) ? res.data : [];
     setProductGroups(rows.map(r => ({ id: r.id, name: r.name })));
   } catch (e) {
     console.error('Fehler beim Laden der Produktgruppen:', e);
     setProductGroups([]);
   }
 };

 // ▼ NEU: Maschinentypen laden (robust, probiert mehrere Endpunkte)
 const fetchMachineTypes = async () => {
   const tryEndpoints = ['/machinetypes', '/machine-types', '/machinetypes/used'];
   for (const ep of tryEndpoints) {
     try {
       const res = await api.get(ep);
       const arr = Array.isArray(res.data) ? res.data : [];
       if (arr.length) {
         setMachineTypes(arr.map(r => ({ id: r.id, name: r.name })));
         return;
       }
     } catch {}
   }
   console.error('Konnte Maschinentypen nicht laden (prüfe Backend-Route).');
   setMachineTypes([]);
 };

  useEffect(() => {
    fetchUsers();
    fetchCustomers();
    fetchProductGroups();
    fetchMachineTypes();
  }, []);

  // NEW: Kontakte eines Kunden laden (für das Kontakt-Dropdown)
  const loadContactsForCustomer = async (customerId) => {
    if (!customerId) {
      setCustomerContacts([]);
      return;
    }
    setContactsLoading(true);
    try {
      const res = await api.get(`/contacts/customer/${customerId}`);
      const arr = Array.isArray(res.data) ? res.data : [];
setCustomerContacts(arr.filter(ct =>
  String(ct?.contactFunction || '').trim().toLowerCase() === 'besteller'
));

    } catch (e) {
      console.error('Fehler beim Laden der Kontakte:', e);
      setCustomerContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const handleDeleteUser = async (userOrId) => {
  const userObj = typeof userOrId === 'object' ? userOrId : users.find(u => u.id === userOrId);
  if (!userObj) return;

  // Nur Admin braucht Passwort
  if (isAdminAccount(userObj)) {
    setAdminToDelete(userObj);
    setAdminDeletePassword('');
    setAdminDeleteError('');
    setShowAdminDeleteModal(true);
    return;
  }

  // Nicht-Admin: wie bisher mit Confirm
  if (!window.confirm('Benutzer wirklich löschen?')) return;
  try {
    await api.delete(`/users/${userObj.id}`);
    setUsers(prev => prev.filter(u => u.id !== userObj.id));
  } catch (error) {
    console.error('Fehler beim Löschen:', error);
    alert(
   error?.response?.data?.error ||
   error?.response?.data?.message ||
   'Fehler beim Löschen'
 );
  }
};


const confirmDeleteAdmin = async () => {
  if (!adminToDelete) return;
  setAdminDeleting(true);
  setAdminDeleteError('');
  try {
    // DELETE mit Body: axios → { data: {...} }
    await api.delete(`/users/${adminToDelete.id}`, {
      data: { password: adminDeletePassword },
    });
    setUsers(prev => prev.filter(u => u.id !== adminToDelete.id));
    setShowAdminDeleteModal(false);
    setAdminDeletePassword('');
    setAdminToDelete(null);
  } catch (error) {
    console.error('Admin-Löschung fehlgeschlagen:', error);
    setAdminDeleteError(
   error?.response?.data?.error ||
   error?.response?.data?.message ||
   'Löschen fehlgeschlagen.'
 );
  } finally {
    setAdminDeleting(false);
  }
};



  const handleEditUser = (user) => {
    setEditingUserId(user.id);
    setEditedUser({
  ...user,
  username: user.username || '',
  password: '',
  passwordConfirm: '',
  currentPassword: '',
  email: user.email || '',
  phone: user.phone || '',
  mobile: user.mobile || '',
  customerId: user.customerId ?? null,
  contactId: user.contactId ?? null,
  allowedProductGroupIds: Array.isArray(user.allowedProductGroupIds) ? user.allowedProductGroupIds : [],
  allowedMachineTypeIds:  Array.isArray(user.allowedMachineTypeIds)  ? user.allowedMachineTypeIds  : [],
});

    // NEW: Beim Start des Editierens ggf. die Kontakte des Kunden laden
    if (user.customerId) {
      loadContactsForCustomer(user.customerId);
    } else {
      setCustomerContacts([]);
    }
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditedUser({});
    setCustomerContacts([]); // NEW
  };

  const handleUpdateUser = async (id) => {
    try {
      const targetUser = getUserById(id);
   const isAdminTarget = isAdminAccount(targetUser);
          const {
      username,
      password,
      passwordConfirm,
      currentPassword,
      firstName,
      lastName,
      email,
      phone,
      mobile,
      customerId,
      contactId,
      allowedProductGroupIds,
      allowedMachineTypeIds
    } = editedUser;

    // einfache Frontend-Validierung
    if (!username || String(username).trim() === '') {
      alert('Username darf nicht leer sein.');
      return;
    }
    if (password && password !== passwordConfirm) {
      alert('Passwörter stimmen nicht überein.');
      return;
    }

     // Wenn Admin-User editiert wird → Bestätigung verlangen
 if (isAdminTarget && (!currentPassword || String(currentPassword).trim() === '')) {
   alert('Zum Bearbeiten des System-Admins bitte das aktuelle Passwort eingeben.');
   return;
 }

      const payload = {
        username: String(username).trim(),
        firstName,
        lastName,
        email: email && email.trim() !== '' ? email.trim() : null,
        phone,
        mobile,
        customerId:
          (customerId === '' || customerId === null || customerId === undefined)
            ? null
            : Number(customerId),
        // NEW: contactId nur setzen, wenn ein Kunde gewählt wurde; sonst null
        contactId:
          (customerId && contactId !== '' && contactId !== null && contactId !== undefined)
            ? Number(contactId)
            : null,
        allowedProductGroupIds: Array.isArray(allowedProductGroupIds) ? allowedProductGroupIds.map(Number) : null,
        allowedMachineTypeIds:  Array.isArray(allowedMachineTypeIds)  ? allowedMachineTypeIds.map(Number)  : null,
      };
      if (password && String(password).trim() !== '') {
      payload.password = String(password);
      }

       if (isAdminTarget) {
   payload.currentPassword = String(currentPassword || '');
 }

      await api.put(`/users/${id}`, payload);
      setEditingUserId(null);
      setCustomerContacts([]); // NEW
      fetchUsers();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      const msg = error?.response?.data?.error || error?.response?.data?.message || 'Fehler beim Speichern';
      alert(msg);
    }
  };

  const handleChange = (field, value) => {
    setEditedUser(prev => ({ ...prev, [field]: value }));
  };

  // Portal-Toggle = Kunde gesetzt?
  const handlePortalToggle = (checked) => {
    if (checked) {
      // NEW: bei Aktivierung Kunde leer lassen (muss gewählt werden) & Kontakte leeren
      setEditedUser(prev => ({ ...prev, customerId: prev.customerId ?? '', contactId: null }));
      setCustomerContacts([]);
    } else {
      // NEW: bei Deaktivierung Portal Felder löschen
      setEditedUser(prev => ({ ...prev, customerId: null, contactId: null }));
      setCustomerContacts([]);
    }
  };

  // NEW: Wenn im Edit-Mode der Kunde gewechselt wird → Kontakte laden & contactId zurücksetzen
  const handleCustomerChange = async (value) => {
    handleChange('customerId', value);
    handleChange('contactId', null);
    await loadContactsForCustomer(value || null);
  };

  // NEU: Debounced Filter für Kunden, wie in OfferForm
const filterCustomers = useMemo(() => debounce((inputValue = '') => {
  const q = inputValue.trim().toLowerCase();
  if (!q) return setFilteredCustomers(customers);

  setFilteredCustomers(
    customers.filter((c) =>
      `${c.company} ${c.customerNumber} ${c.firstName} ${c.lastName} ${c.street} ${c.postalCode} ${c.city}`
        .toLowerCase()
        .includes(q)
    )
  );
}, 300), [customers]);


// NEU: onInputChange-Wrapper für react-select
const handleCustomerSearchChange = (inputValue) => {
  filterCustomers(inputValue);
};


// ✅ HIER rein
useEffect(() => {
  return () => filterCustomers.cancel();
}, [filterCustomers]);

// Toggle-Helfer
const toggleIdInArray = (arr, id) =>
  (arr || []).includes(id) ? (arr || []).filter(x => x !== id) : [ ...(arr || []), id ];

const handleTogglePG = (id) => {
  setEditedUser(prev => ({
    ...prev,
    allowedProductGroupIds: toggleIdInArray(prev.allowedProductGroupIds, id),
  }));
};

const handleToggleMT = (id) => {
  setEditedUser(prev => ({
    ...prev,
    allowedMachineTypeIds: toggleIdInArray(prev.allowedMachineTypeIds, id),
  }));
};


  // Multi-Select <select multiple> → Array<number> (IDs)
const selectedOptionsToIds = (selectEl) => {
  const ids = Array.from(selectEl.selectedOptions).map(o => Number(o.value));
  return ids.filter(Number.isInteger);
};

// IDs -> kommagetrennte Labelnamen (für Read-Ansicht)
const labelsFromIds = (ids, options) => {
  if (!Array.isArray(ids) || !ids.length) return '—';
  const map = new Map(options.map(o => [String(o.id), o.name]));
  return ids.map(id => map.get(String(id)) || `#${id}`).join(', ');
};


  const resolveCustomerName = (cid) => {
    if (!cid) return '—';
    const c = customers.find(cu => String(cu.id) === String(cid));
    return c ? `${c.company} (#${c.id})` : `Kunde #${cid}`;
  };

  // NEW: Hilfsanzeige des Kontaktnamens in der Liste (Read-Mode)
  const resolveContactName = (customerId, contactId) => {
    if (!customerId || !contactId) return '—';
    // Wir versuchen pragmatisch den Kontakt-Namen aus customerContacts oder zeigen nur die ID.
    // (Optional: Man könnte hier auch alle Kontakte puffern; für jetzt minimal.)
    const c = customerContacts.find(ct => String(ct.id) === String(contactId));
    if (c) {
      const fn = c.contactFirstName || c.firstName || '';
      const ln = c.contactLastName || c.lastName || '';
      return `${fn} ${ln}`.trim() || `Kontakt #${contactId}`;
    }
    return `Kontakt #${contactId}`;
  };

  // NEU: Optionen für das Kunde-Kombi-Select
const customerOptions = (filteredCustomers || []).map((c) => ({
  value: String(c.id),
  label: `${c.company} (${c.customerNumber})`,
}));

  if (loading) return <div>Daten werden geladen...</div>;

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
      <div style={{ width: '100%', maxWidth: '1600px' }}>
        <Helmet>
          <title>Benutzer verwalten</title>
        </Helmet>

        <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>Benutzer verwalten</h2>

        <ul style={{ listStyle: 'none', padding: 0 }}>
          {users.map(user => {
            const isEditing = editingUserId === user.id;
            const hasCustomer = (v) => v !== null && v !== undefined && String(v).trim() !== '';

            const isPortal = isEditing
              ? hasCustomer(editedUser.customerId)
              : hasCustomer(user.customerId);


            return (
              <li
                key={user.id}
                style={{
                  display: 'grid',
                  // ID | Username | Name | Email | Telefon | Mobil | Portal? | Kunde | Kontakt | Actions
                  gridTemplateColumns: '60px 240px minmax(360px,2fr) 200px 120px 120px 100px 220px 220px auto',
                  gap: '10px',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: '1px solid #ddd'
                }}
              >
                <span>{user.id}</span>
                {isEditing ? (
                <input
                  value={editedUser.username || ''}
                  onChange={e => handleChange('username', e.target.value)}
                  placeholder="Username"
                  style={{ width: '100%' }}
                />
              ) : (
                <span>{user.username}</span>
              )}

                {isEditing ? (
                  <>
                  {/* Passwort ändern (optional) */}
    <div
      style={{
        gridColumn: '1 / -1',
        marginTop: 8,
        background: '#fff8e1',
        border: '1px solid #f0c36d',
        borderRadius: 6,
        padding: 10
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Passwort ändern (optional)</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          type="password"
          value={editedUser.password || ''}
          onChange={e => handleChange('password', e.target.value)}
          placeholder="Neues Passwort"
          autoComplete="new-password"
          style={{ flex: '1 1 240px', minWidth: 220 }}
        />
        <input
          type="password"
          value={editedUser.passwordConfirm || ''}
          onChange={e => handleChange('passwordConfirm', e.target.value)}
          placeholder="Passwort bestätigen"
          autoComplete="new-password"
          style={{ flex: '1 1 240px', minWidth: 220 }}
        />
      </div>
      <div style={{ fontSize: 12, opacity: 0.8, marginTop: 6 }}>
        Leer lassen, wenn kein Passwortwechsel gewünscht ist.
      </div>
    </div>

    {isEditing && isAdminAccount(user) && (
  <div
    style={{
      gridColumn: '1 / -1',
      marginTop: 8,
      background: '#ffecec',
      border: '1px solid #f5b1b1',
      borderRadius: 6,
      padding: 10
    }}
  >
    <div style={{ fontWeight: 600, marginBottom: 6 }}>
      Admin-Bestätigung
    </div>
    <input
      type="password"
      value={editedUser.currentPassword || ''}
      onChange={e => handleChange('currentPassword', e.target.value)}
      placeholder="Aktuelles Admin-Passwort eingeben"
      autoComplete="current-password"
      style={{ width: '100%' }}
    />
    <div style={{ fontSize: 12, opacity: 0.8, marginTop: 6 }}>
      Zum Bearbeiten des System-Admins ist das aktuelle Passwort erforderlich.
    </div>
  </div>
)}
                    {/* Name */}
                     {/* Name (spannt über 2 Spalten) */}
 <div
   style={{
     display: 'grid',
     gridTemplateColumns: '1fr 1fr',
     columnGap: 8,
     gridColumn: '1 / span 2',   // ← nimmt Spalte 3 UND 4 ein
     minWidth: 0,
     maxWidth: 'unset'
   }}
 >
                      <input
                        value={editedUser.firstName || ''}
                        onChange={e => handleChange('firstName', e.target.value)}
                        placeholder="Vorname"
                        style={{ width: '100%', minWidth: 0, maxWidth: 'unset' }}
                      />
                      <input
                        value={editedUser.lastName || ''}
                        onChange={e => handleChange('lastName', e.target.value)}
                        placeholder="Nachname"
                        style={{ width: '100%', minWidth: 0, maxWidth: 'unset' }}
                      />
                    </div>

                    {/* Email */}
                    <input
                      value={editedUser.email || ''}
                      onChange={e => handleChange('email', e.target.value)}
                      placeholder="E-Mail"
                    />

                    {/* Telefon */}
                    <input
                      value={editedUser.phone || ''}
                      onChange={e => handleChange('phone', e.target.value)}
                      placeholder="Telefon"
                      style={{ width: '100%', maxWidth: 'unset', minWidth: 0 }}
                    />

                    {/* Mobil */}
                    <input
                      value={editedUser.mobile || ''}
                      onChange={e => handleChange('mobile', e.target.value)}
                      placeholder="Mobil"
                    />

                    {/* Portal + Kunde + Kontakt: eine Zeile darunter, über volle Breite */}
<div style={{ gridColumn: '1 / -1' }}>
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: '220px minmax(420px, 2fr) 1fr',
      gap: 10,
      alignItems: 'center',
    }}
  >
    {/* Portal-Toggle */}
    <div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          type="checkbox"
          checked={isPortal}
          onChange={e => handlePortalToggle(e.target.checked)}
        />
        Portal-Benutzer
      </label>
    </div>

    {/* Kunde (react-select) */}
    <div style={{ position: 'relative', minWidth: 420 }}>
      {isPortal ? (
        <Select
          id="customerId"
          name="customerId"
          options={customerOptions}
          onInputChange={handleCustomerSearchChange}
          onChange={(opt) => handleCustomerChange(opt ? opt.value : '')}
          value={
            customerOptions.find(o => String(o.value) === String(editedUser.customerId)) || null
          }
          isClearable
          placeholder="Kunde suchen…"
          className="select-box-cst"
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          menuPosition="fixed"
          menuPlacement="auto"
          menuShouldScrollIntoView={false}
          styles={{
            container: (base) => ({ ...base, width: '100%' }),
            valueContainer: (base) => ({ ...base, minWidth: 0 }),
            control:   (base) => ({ ...base, minHeight: 36, width: '60%' }),
            menu: (base) => ({ ...base, zIndex: 9999 }),
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />
      ) : (
        <em>—</em>
      )}
    </div>

    {/* Ansprechpartner (native select) */}
    <div>
      {isPortal && editedUser.customerId ? (
        contactsLoading ? (
          <span>Lade Kontakte…</span>
        ) : (
          <select
            value={editedUser.contactId ?? ''}
            onChange={e => handleChange('contactId', e.target.value)}
            style={{ width: '50%' }}
          >
            <option value="">– Ansprechpartner (optional) –</option>
            {customerContacts.map(ct => (
              <option key={ct.id} value={String(ct.id)}>
                {(ct.contactFirstName || ct.firstName || '') + ' ' + (ct.contactLastName || ct.lastName || '')}
              </option>
            ))}
          </select>
        )
      ) : (
        <em>—</em>
      )}
    </div>
  </div>
</div>


                      {/* ▼ Unterzeile: Freigaben – nur im Edit, optional nur für Portalnutzer */}
{isPortal && (
  <div
    style={{
      gridColumn: '1 / -1',     // über die ganze Zeile spannen
      marginTop: 8,
      background: '#f8f9fa',
      border: '1px solid #ddd',
      borderRadius: 6,
      padding: 10
    }}
  >
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      {/* Produktgruppen */}
      <div style={{ flex: '1 1 380px', minWidth: 320 }}>
        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
          Produktgruppen (Freigabe)
        </div>
        <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #ccc', padding: 8, borderRadius: 4 }}>
          {productGroups.length === 0 && (
            <em style={{ opacity: 0.7 }}>Keine Produktgruppen geladen</em>
          )}
          {productGroups.map(pg => (
            <label key={pg.id} style={{ display: 'block', fontSize: '0.9em', marginBottom: 6 }}>
              <input
                type="checkbox"
                checked={(editedUser.allowedProductGroupIds || []).includes(pg.id)}
                onChange={() => handleTogglePG(pg.id)}
                style={{ marginRight: 6 }}
              />
              {pg.name}
            </label>
          ))}
        </div>
      </div>

      {/* Maschinentypen */}
      <div style={{ flex: '1 1 380px', minWidth: 320 }}>
        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
          Maschinentypen (Freigabe)
        </div>
        <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #ccc', padding: 8, borderRadius: 4 }}>
          {machineTypes.length === 0 && (
            <em style={{ opacity: 0.7 }}>Keine Maschinentypen geladen</em>
          )}
          {machineTypes.map(mt => (
            <label key={mt.id} style={{ display: 'block', fontSize: '0.9em', marginBottom: 6 }}>
              <input
                type="checkbox"
                checked={(editedUser.allowedMachineTypeIds || []).includes(mt.id)}
                onChange={() => handleToggleMT(mt.id)}
                style={{ marginRight: 6 }}
              />
              {mt.name}
            </label>
          ))}
        </div>
      </div>
    </div>
  </div>
)}

                    {/* Actions */}
                    <div>
                      <button onClick={() => handleUpdateUser(user.id)} style={{ marginRight: '5px' }}>✅</button>
                      <button onClick={handleCancelEdit}>✖️</button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Name */}
                    <span>{user.firstName} {user.lastName}</span>
                    {/* Email */}
                    <span>{user.email || '—'}</span>
                    {/* Telefon */}
                    <span>{user.phone || '—'}</span>
                    {/* Mobil */}
                    <span>{user.mobile || '—'}</span>
                    {/* Portal? */}
                    <span>{user.customerId ? 'Ja' : 'Nein'}</span>
                    {/* Kunde */}
                    <span>{resolveCustomerName(user.customerId)}</span>
                    {/* NEW: Kontakt (nur Anzeige – Name wenn bekannt, sonst ID) */}
                    <span>
                      {user.contactId ? `Kontakt #${user.contactId}` : '—'}
                    </span>
                    
                    {/* Actions */}
                    <div>
                      <button
                        onClick={() => handleEditUser(user)}
                        style={{
                          fontSize: '14px',
                          padding: '4px 10px',
                          marginRight: '5px',
                          background: '#007bff',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user)}
                        style={{
                          fontSize: '14px',
                          padding: '4px 10px',
                          background: '#dc3545',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Löschen
                      </button>
                    </div>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </div>
{/* ⬇️ HIER das Admin-Delete-Modal einfügen (zwischen innerem und äußerem </div>) */}
    {showAdminDeleteModal && (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
      }}>
        <div style={{ background: '#fff', borderRadius: 10, padding: 20, minWidth: 380, boxShadow: '0 4px 18px rgba(0,0,0,0.15)' }}>
          <h3 style={{ marginTop: 0 }}>Admin löschen bestätigen</h3>
          <p>
            Bitte das Passwort des Admin-Accounts eingeben, um{' '}
            <strong>{adminToDelete?.username}</strong> zu löschen.
          </p>

          <input
            type="password"
            value={adminDeletePassword}
            onChange={(e) => setAdminDeletePassword(e.target.value)}
            placeholder="Admin-Passwort"
            style={{ width: '100%', padding: 8, boxSizing: 'border-box' }}
            autoFocus
            onKeyDown={(e) => {
   if (e.key === 'Enter' && !adminDeleting && adminDeletePassword) {
     confirmDeleteAdmin();
   }
 }}
          />

          {adminDeleteError && (
            <div style={{ color: '#c92a2a', marginTop: 8 }}>
              {adminDeleteError}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <button
              onClick={() => {
                setShowAdminDeleteModal(false);
                setAdminToDelete(null);
                setAdminDeletePassword('');
                setAdminDeleteError('');
              }}
            >
              Abbrechen
            </button>
            <button
              onClick={confirmDeleteAdmin}
              disabled={adminDeleting || !adminDeletePassword}
              style={{ background: '#dc3545', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4 }}
            >
              {adminDeleting ? 'Löschen…' : 'Endgültig löschen'}
            </button>
          </div>
        </div>
      </div>
    )}
    {/* ⬆️ Ende Modal */}

    </div>
  );
};

export default UserForm;
